import { SubscriptionPlanIdentifier, SubscriptionPlanType } from "src/app/core/models/stripe.models";

/**
 * Resolve a `/new-plan/:id` / `/payment-details/:id` route param to a plan.
 *
 * Route params carry enum KEYS ("SalesPro"), but URLs produced before the
 * #421/#422 fixes carried enum VALUES ("sales-pro") and can survive in
 * history entries and bookmarks — accept both so those pages still render.
 */
export function planTypeFromRouteParam(param: string | null): SubscriptionPlanType | undefined {
  if (!param) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(SubscriptionPlanType, param)) {
    return SubscriptionPlanType[param];
  }
  return (Object.values(SubscriptionPlanType) as string[]).includes(param)
    ? (param as SubscriptionPlanType)
    : undefined;
}

/**
 * The enum KEY for a plan ("SalesPro" for `sales-pro`) — the only form that
 * `planTypeFromRouteParam`-style key lookups resolve, so the form every
 * router.navigate(...) to a plan page must carry.
 */
export function planTypeToRouteKey(planType: SubscriptionPlanType): string {
  return (
    Object.keys(SubscriptionPlanType).find((key) => SubscriptionPlanType[key] === planType) ??
    planType
  );
}

/**
 * UI plan -> backend plan identifier.
 *
 * `SubscriptionPlanType` is the route/UI vocabulary and `SubscriptionPlanIdentifier`
 * is the backend's; the two only *look* interchangeable. Monthly Sales Pro is where
 * the resemblance breaks — its plan type is `"sales-pro"`, which is not a plan the
 * API knows (`v1/subscriptions/*` answers "No active plan found for identifier
 * 'sales-pro'"). Hence an explicit table rather than string munging: every previous
 * attempt to derive one vocabulary from the other by lower-casing or by scanning the
 * enums missed exactly this plan and failed silently (#424).
 *
 * The Teams plans are intentionally absent — they are sales-led and never bought
 * through this checkout, so they have no self-serve identifier to map to.
 */
const PLAN_TYPE_TO_IDENTIFIER: Partial<Record<SubscriptionPlanType, SubscriptionPlanIdentifier>> = {
  [SubscriptionPlanType.Freemium]: SubscriptionPlanIdentifier.Freemium,
  [SubscriptionPlanType.Starter]: SubscriptionPlanIdentifier.Starter,
  [SubscriptionPlanType.YearlyStarter]: SubscriptionPlanIdentifier.YearlyStarter,
  [SubscriptionPlanType.Growth]: SubscriptionPlanIdentifier.Growth,
  [SubscriptionPlanType.YearlyGrowth]: SubscriptionPlanIdentifier.YearlyGrowth,
  [SubscriptionPlanType.SalesPro]: SubscriptionPlanIdentifier.SalesPro,
  [SubscriptionPlanType.MonthlySalesPro]: SubscriptionPlanIdentifier.SalesPro,
  [SubscriptionPlanType.YearlySalesPro]: SubscriptionPlanIdentifier.YearlySalesPro,
};

/**
 * The backend identifier for a plan, or `undefined` when the plan has none.
 *
 * Callers must handle `undefined` rather than substituting a default: the bug this
 * replaced defaulted to `"freemium"`, which is itself a real plan, so an unmapped
 * plan produced a confident answer about the wrong subscription instead of an error.
 */
export function planTypeToIdentifier(
  planType: SubscriptionPlanType | undefined
): SubscriptionPlanIdentifier | undefined {
  return planType ? PLAN_TYPE_TO_IDENTIFIER[planType] : undefined;
}

/**
 * `StripeService.getCurrentPlan()` (`GET v2/plans/current`) rewrites
 * `"sales-pro-monthly"` (and the already-normalised `"sales-pro"`) to
 * `"sales-pro"` on the object it returns — the only identifier its
 * normalisation actually changes (issue #609's "Contracts pinned" table).
 * A caller comparing that returned `identifier` against
 * `planTypeToIdentifier()`'s answer (`SubscriptionPlanIdentifier.SalesPro`
 * = `"sales-pro-monthly"`) must apply the SAME rewrite to both sides first,
 * or a purchased Sales Pro plan can never be recognised as converged.
 */
export function normalizeIdentifierForCurrentPlanMatch(identifier: string | undefined): string | undefined {
  return identifier === "sales-pro-monthly" || identifier === "sales-pro" ? "sales-pro" : identifier;
}
